import { Link, useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  PageHeader,
  SectionCard,
  StatTile,
} from "@/features/power_projects/components/ui";

import {
  DocumentsSection,
  RecommendationBlock,
  RowDetail,
  SimilarSection,
} from "../components";
import { useEzProceeding } from "../hooks";
import {
  DeadlinePill,
  DecisionControl,
  StageChip,
  TagChip,
  WorkModeChip,
  fmtPln,
} from "../ui";

/* Dedicated page of one proceeding — everything EZ-ARON knows about it. */
export default function EzProceedingPage() {
  const { id = "" } = useParams();
  const detail = useEzProceeding(id);

  if (detail.isLoading) return <PageLoader />;
  const d = detail.data;
  if (!d)
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-red-600 mb-3">
          Nie znaleziono postępowania.
        </p>
        <Link to="/ezamowienia">
          <Button variant="secondary">← Wróć do okazji</Button>
        </Link>
      </div>
    );

  // totals_snapshot is nested: {min: {total, …}, avg: {…}, max: {…}}
  const rawTotals = d.estimate?.totals as Record<
    string,
    { total?: string } | undefined
  > | null;
  const variant = (k: "min" | "avg" | "max") => {
    const v = Number(rawTotals?.[k]?.total);
    return Number.isFinite(v) && v > 0 ? fmtPln(String(v)) : null;
  };
  const hasTotals = Boolean(variant("min") || variant("avg") || variant("max"));

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <PageHeader
        title={d.title}
        backTo="/ezamowienia"
        backLabel="Oferty przetargowe"
        actions={
          <div className="flex items-center gap-2">
            <DeadlinePill row={d} />
            {d.source_url && (
              <a href={d.source_url} target="_blank" rel="noreferrer">
                <Button variant="secondary">
                  Platforma <ExternalLink size={12} className="inline ml-1" />
                </Button>
              </a>
            )}
          </div>
        }
      />

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StageChip stage={d.stage} />
          <WorkModeChip mode={d.work_mode} />
          {d.tags.map((t) => (
            <TagChip key={t} tag={t} />
          ))}
        </div>
        <DecisionControl row={d} />
      </div>

      {d.recommendation && (
        <div className="mb-4">
          <RecommendationBlock row={d} framed />
        </div>
      )}

      {d.estimate && (
        <SectionCard title="Wycena z przedmiaru (SEKOCENBUD)" className="mb-4">
          {hasTotals ? (
            <div className="grid grid-cols-3 gap-3 mb-3">
              <StatTile
                label="Wariant minimalny"
                value={variant("min") ?? "—"}
              />
              <StatTile
                label="Wariant średni"
                value={variant("avg") ?? "—"}
                tone="ok"
              />
              <StatTile
                label="Wariant maksymalny"
                value={variant("max") ?? "—"}
              />
            </div>
          ) : d.estimate.status === "ready" ? (
            <p className="text-sm text-amber-700 dark:text-amber-400 mb-3">
              Wycena zakończona bez policzalnych pozycji — sprawdź kosztorys
              niżej (możliwy nieczytelny przedmiar lub brak dopasowań cennika).
            </p>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)] mb-3">
              Wycena w toku (status: {d.estimate.status}) — AI wyciąga pozycje,
              rozwija normy KNR i liczy warianty.
            </p>
          )}
          <Link to={`/estimating/${d.estimate.job_id}`}>
            <Button variant="secondary">
              Otwórz kosztorys (pozycje, nakłady, audyt AI) →
            </Button>
          </Link>
        </SectionCard>
      )}

      <SectionCard title="Postępowanie" className="mb-4" bodyClassName="pt-1">
        <RowDetail id={id} />
      </SectionCard>

      <SectionCard title="Analiza cenowa i dokumenty" bodyClassName="pt-1">
        <SimilarSection id={id} autoOpen />
        <DocumentsSection id={id} autoOpen />
      </SectionCard>
    </div>
  );
}
